/**
 * Plugin System Type Definitions
 * Version: 1.0
 *
 * Defines the core interfaces for the plugin architecture,
 * following software addon best practices with versioning,
 * lifecycle management, and dependency injection.
 */

import { ReactNode } from 'react';

// ============================================================================
// Plugin Manifest & Versioning
// ============================================================================

export interface PluginManifest {
  id: string;
  name: string;
  version: string; // Semantic versioning (1.0.0)
  minSystemVersion: string; // Minimum compatible app version
  maxSystemVersion?: string; // Maximum compatible app version
  description: string;
  author?: string;
  homepage?: string;

  // Dependencies on other plugins
  dependencies?: {
    [pluginId: string]: string; // Plugin ID -> version range (e.g., "1.0.0" or "*")
  };

  // API version this plugin was built for
  pluginApiVersion: string; // e.g., "1.0"
}

// ============================================================================
// Plugin Capabilities & Features
// ============================================================================

export interface PluginCapabilities {
  // Data access requirements
  requiredEndpoints: ('clubs' | 'valuations' | 'player-sales')[];
  requiredPermissions: string[]; // e.g., ['read:valuations', 'read:clubs']

  // Features this plugin provides
  features: {
    reports?: boolean;
    dashboard?: boolean;
    valuations?: boolean;
    analytics?: boolean;
    exports?: boolean;
  };

  // Resource limits
  resourceLimits?: {
    maxMemoryMB?: number;
    maxExecutionTimeMs?: number;
    maxApiCallsPerMinute?: number;
  };
}

// ============================================================================
// Plugin Lifecycle
// ============================================================================

export interface PluginLifecycle {
  // Lifecycle hooks
  onInstall?: () => Promise<void>;
  onUninstall?: () => Promise<void>;
  onEnable?: () => Promise<void>;
  onDisable?: () => Promise<void>;
  onUpdate?: (oldVersion: string, newVersion: string) => Promise<void>;

  // Health checks
  healthCheck?: () => Promise<PluginHealth>;
}

export interface PluginHealth {
  status: 'healthy' | 'degraded' | 'unhealthy';
  message?: string;
  lastCheck: Date;
  dependencies?: {
    [key: string]: 'available' | 'unavailable';
  };
}

// ============================================================================
// Plugin Settings
// ============================================================================

export interface PluginSettings<TConfig = Record<string, unknown>> {
  component: React.ComponentType<PluginSettingsProps<TConfig>>;
  schema: JSONSchema; // For validation
  defaultConfig: TConfig;
  migrations?: {
    [fromVersion: string]: (config: unknown) => unknown;
  };
}

export interface PluginSettingsProps<TConfig = Record<string, unknown>> {
  config: TConfig;
  onSave: (config: TConfig) => Promise<void>;
  onTest?: () => Promise<boolean>;
}

// JSON Schema for config validation
export interface JSONSchema {
  type: 'object' | 'string' | 'number' | 'boolean' | 'array';
  properties?: {
    [key: string]: unknown;
  };
  required?: string[];
  enum?: unknown[];
  items?: unknown;
}

// ============================================================================
// Plugin Context (Dependency Injection)
// ============================================================================

export interface PluginContext {
  organizationId: string;
  apiClient?: UniversalAPIClient; // Optional - only available if API key is configured
  cache: PluginCache;
  logger: PluginLogger;
  events: PluginEventBus;
  storage: PluginStorage;
}

// ============================================================================
// Universal API Client (All Endpoints)
// ============================================================================

export interface ClubData {
  id: string;
  name: string;
  country: string;
  league: string;
  tier: number;
  badge_url?: string;
  website?: string;
  founded_year?: number;
}

export interface ValuationData {
  id: string;
  organization_id: string;
  valuation_date: string;
  final_total_value: number;
  calculated_sales_value: number;
  calculated_media_value: number;
  qualitative_multiplier: number;
  notes?: string;
  created_at?: string;
  updated_at?: string;
}

export interface PlayerSaleData {
  id: string;
  valuation_id: string;
  player_name: string;
  sale_amount: number;
  buyer_club: string;
  sale_date: string;
  position?: string;
  age?: number;
  contract_years?: number;
}

export interface ComprehensiveReportData {
  club: ClubData;
  valuations: ValuationData[];
  playerSales: PlayerSaleData[];
  summary: {
    totalValuations: number;
    totalSales: number;
    averageValue: number;
    trend: 'up' | 'down' | 'stable';
  };
}

export interface UniversalAPIClient {
  getClub(organizationId: string): Promise<ClubData>;
  getValuations(
    organizationId: string,
    options?: {
      fromDate?: string;
      toDate?: string;
      limit?: number;
      offset?: number;
    }
  ): Promise<ValuationData[]>;
  getPlayerSales(
    organizationId: string,
    options?: {
      valuationId?: string;
      fromDate?: string;
      toDate?: string;
    }
  ): Promise<PlayerSaleData[]>;
  getComprehensiveReport(
    organizationId: string,
    options?: {
      fromDate?: string;
      toDate?: string;
    }
  ): Promise<ComprehensiveReportData>;
}

// ============================================================================
// Plugin Cache
// ============================================================================

export interface PluginCache {
  get<T>(key: string): Promise<T | null>;
  set<T>(key: string, value: T, ttlSeconds?: number): Promise<void>;
  delete(key: string): Promise<void>;
  clear(): Promise<void>;
}

// ============================================================================
// Plugin Logger
// ============================================================================

export interface PluginLogger {
  debug(message: string, ...args: unknown[]): void;
  info(message: string, ...args: unknown[]): void;
  warn(message: string, ...args: unknown[]): void;
  error(message: string, error?: Error, ...args: unknown[]): void;
}

// ============================================================================
// Plugin Event Bus
// ============================================================================

export type PluginEvent =
  | { type: 'valuation.created'; data: ValuationData }
  | { type: 'valuation.updated'; data: ValuationData }
  | { type: 'valuation.deleted'; data: { id: string } }
  | { type: 'player-sale.created'; data: PlayerSaleData }
  | { type: 'player-sale.updated'; data: PlayerSaleData }
  | { type: 'player-sale.deleted'; data: { id: string } }
  | { type: 'report.generated'; data: { reportId: string; organizationId: string } }
  | { type: 'plugin.enabled'; data: { pluginId: string; organizationId: string } }
  | { type: 'plugin.disabled'; data: { pluginId: string; organizationId: string } };

export interface PluginEventBus {
  subscribe(eventType: string, handler: (event: PluginEvent) => void | Promise<void>): () => void;
  emit(event: PluginEvent): Promise<void>;
}

// ============================================================================
// Plugin Storage
// ============================================================================

export interface PluginStorage {
  get<T>(key: string): Promise<T | null>;
  set<T>(key: string, value: T): Promise<void>;
  delete(key: string): Promise<void>;
  clear(): Promise<void>;
  getAll<T = Record<string, unknown>>(): Promise<T | null>;
  setAll<T = Record<string, unknown>>(config: T): Promise<void>;
  keys(): Promise<string[]>;
}

// ============================================================================
// Main Plugin Interface
// ============================================================================

export interface Plugin<TConfig = Record<string, unknown>> {
  manifest: PluginManifest;
  capabilities: PluginCapabilities;
  lifecycle: PluginLifecycle;

  enabled: boolean;

  // Settings
  settings?: PluginSettings<TConfig>;

  // Internal state (not part of manifest)
  currentConfig?: TConfig;
  context?: PluginContext;

  // Initialize plugin with dependency injection
  initialize(context: PluginContext): Promise<void>;
  cleanup(): Promise<void>;
}

// ============================================================================
// Reports Plugin (extends base Plugin)
// ============================================================================

export interface ReportsPlugin<TConfig = Record<string, unknown>> extends Plugin<TConfig> {
  renderInsights(data: ReportData): Promise<ReactNode | null>;
}

export interface ReportData {
  organizationId: string;
  locale: 'en' | 'da';
  fromDate?: string;
  toDate?: string;
  [key: string]: unknown; // Allow additional data
}

// ============================================================================
// Plugin Registry Database Schema
// ============================================================================

export interface PluginSettingsDB {
  id: string;
  organization_id: string;
  plugin_id: string;
  plugin_version: string;
  enabled: boolean;
  config: unknown; // JSONB

  // Metadata
  installed_at: string;
  enabled_at?: string;
  disabled_at?: string;
  last_health_check?: string;
  health_status?: 'healthy' | 'degraded' | 'unhealthy';

  // Resource tracking
  last_execution_time_ms?: number;
  total_executions: number;
  total_errors: number;

  created_at: string;
  updated_at: string;
}

// ============================================================================
// API Errors
// ============================================================================

export class APIError extends Error {
  constructor(
    message: string,
    public statusCode: number,
    public endpoint?: string
  ) {
    super(message);
    this.name = 'APIError';
  }
}

export class PluginError extends Error {
  constructor(
    message: string,
    public pluginId: string,
    public code?: string
  ) {
    super(message);
    this.name = 'PluginError';
  }
}
